import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { WarningCircle } from 'phosphor-react';
import { toast } from 'sonner';

import { IApiRecurso, IApiTokenCriado, useApiController } from '../../../hooks/useApiController';
import { IDataNoPagination } from '../../../types/shared';
import { ClienteUsuarioSelect, IUsuarioLite } from './ClienteUsuarioSelect';
import { CopyButton } from './CopyButton';
import { EmpresaExternalIdSelect, IEmpresaExternal } from './EmpresaExternalIdSelect';
import { gerarSnippetCurl, gerarSnippetPython } from './gerarSnippet';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function TokenFormModal({ isOpen, onClose, onCreated }: Props) {
  const { getRecursos, createToken } = useApiController();

  const [recursoId, setRecursoId] = useState<number | ''>('');
  const [colunasPorTabela, setColunasPorTabela] = useState<Record<string, string[]>>({});
  const [empresas, setEmpresas] = useState<IEmpresaExternal[]>([]);
  const [cliente, setCliente] = useState<IUsuarioLite | null>(null);
  const [nome, setNome] = useState('');
  const [criado, setCriado] = useState<IApiTokenCriado | null>(null);

  const { data: recursosData } = useQuery<IDataNoPagination<IApiRecurso[]>>({
    queryKey: ['api-controller-recursos'],
    queryFn: getRecursos,
    enabled: isOpen,
  });
  const recursos = recursosData?.data ?? [];
  const recursoSelecionado = useMemo(
    () => recursos.find(r => r.id === recursoId),
    [recursos, recursoId],
  );

  // Reseta ao (re)abrir/fechar.
  useEffect(() => {
    if (!isOpen) {
      setRecursoId('');
      setColunasPorTabela({});
      setEmpresas([]);
      setCliente(null);
      setNome('');
      setCriado(null);
    }
  }, [isOpen]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createToken,
    onSuccess: response => {
      setCriado(response.data);
      onCreated();
      toast.success('Token criado com sucesso', { position: 'top-right' });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail ?? 'Erro ao criar token', {
        position: 'top-right',
      });
    },
  });

  const toggleColuna = (tabela: string, coluna: string) => {
    setColunasPorTabela(prev => {
      const atual = prev[tabela] ?? [];
      const novo = atual.includes(coluna) ? atual.filter(c => c !== coluna) : [...atual, coluna];
      return { ...prev, [tabela]: novo };
    });
  };

  const toggleTodasColunas = (tabela: string, colunas: string[], marcarTodas: boolean) => {
    setColunasPorTabela(prev => ({ ...prev, [tabela]: marcarTodas ? [...colunas] : [] }));
  };

  const totalColunas = useMemo(
    () => Object.values(colunasPorTabela).reduce((acc, cols) => acc + cols.length, 0),
    [colunasPorTabela],
  );

  const podeSalvar =
    recursoId !== '' &&
    empresas.length > 0 &&
    !!cliente &&
    nome.trim().length >= 2 &&
    totalColunas > 0;

  const handleSubmit = async () => {
    if (!podeSalvar || empresas.length === 0 || !cliente) return;
    // só envia tabelas com pelo menos 1 coluna ON
    const limpo: Record<string, string[]> = {};
    Object.entries(colunasPorTabela).forEach(([t, cols]) => {
      if (cols.length > 0) limpo[t] = cols;
    });
    await mutateAsync({
      usuario_id: cliente.id,
      api_recurso_id: Number(recursoId),
      // multi-empresa: N empresas por token
      external_ids: empresas.map(e => Number(e.external_id)),
      nome: nome.trim(),
      colunas_por_tabela: limpo,
    });
  };

  const tabelasDaRota = recursoSelecionado?.tabelas ?? [];
  // Slug da ROTA escolhida — é ele que entra no path do exemplo (`/external/v1/<slug>/`),
  // não os slugs das tabelas (a rota já serve todas elas numa chamada só).
  const rotaSlug = recursoSelecionado?.slug;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>{criado ? 'Token criado' : 'Criar token da API pública'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {criado ? (
            // ── Sucesso: credenciais + exemplo de código ──────────────────────
            <Box display="flex" flexDirection="column" gap={3}>
              <Box bg="green.50" p={3} borderRadius="md">
                <Text fontWeight="medium" color="green.700">
                  Token criado! Envie a API key e o HMAC secret ao cliente.
                </Text>
              </Box>

              <FormControl>
                <FormLabel mb={1}>API key</FormLabel>
                <Flex gap={2}>
                  <Code p={2} flex={1} overflowX="auto" whiteSpace="nowrap">
                    {criado.api_key}
                  </Code>
                  <CopyButton value={criado.api_key} />
                </Flex>
              </FormControl>

              <FormControl>
                <FormLabel mb={1}>HMAC secret</FormLabel>
                <Flex gap={2}>
                  <Code p={2} flex={1} overflowX="auto" whiteSpace="nowrap">
                    {criado.hmac_secret}
                  </Code>
                  <CopyButton value={criado.hmac_secret} />
                </Flex>
              </FormControl>

              <Divider my={1} />

              <FormLabel mb={0}>Exemplo de chamada (envie ao cliente)</FormLabel>
              <Tabs size="sm" variant="enclosed">
                <TabList>
                  <Tab>cURL</Tab>
                  <Tab>Python</Tab>
                </TabList>
                <TabPanels>
                  {[
                    {
                      lang: 'curl',
                      code: gerarSnippetCurl({
                        apiKey: criado.api_key,
                        hmacSecret: criado.hmac_secret,
                        rotaSlug,
                        baseUrl: criado.base_url,
                      }),
                    },
                    {
                      lang: 'python',
                      code: gerarSnippetPython({
                        apiKey: criado.api_key,
                        hmacSecret: criado.hmac_secret,
                        rotaSlug,
                        baseUrl: criado.base_url,
                      }),
                    },
                  ].map(({ lang, code }) => (
                    <TabPanel key={lang} px={0}>
                      <Flex justify="flex-end" mb={1}>
                        <CopyButton value={code} label="Copiar código" />
                      </Flex>
                      <Code
                        display="block"
                        whiteSpace="pre"
                        overflowX="auto"
                        p={3}
                        borderRadius="md"
                        fontSize="xs"
                      >
                        {code}
                      </Code>
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            </Box>
          ) : (
            // ── Form: rota → colunas → empresa → cliente ──────────────────────
            <Box display="flex" flexDirection="column" gap={4}>
              <FormControl>
                <FormLabel>1. Rota (define as tabelas)</FormLabel>
                <Select
                  placeholder="Selecione a rota"
                  value={recursoId}
                  onChange={e => {
                    setRecursoId(e.target.value ? Number(e.target.value) : '');
                    setColunasPorTabela({});
                  }}
                >
                  {recursos.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.nome}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {recursoSelecionado && (
                <FormControl>
                  <FormLabel>
                    2. Colunas liberadas por tabela
                    {totalColunas > 0 && (
                      <Badge ml={2} colorScheme="blue">
                        {totalColunas} coluna(s)
                      </Badge>
                    )}
                  </FormLabel>
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    Nada é exposto por padrão (allowlist). Ligue só o que o cliente pode ler.
                  </Text>
                  <Box display="flex" flexDirection="column" gap={3}>
                    {tabelasDaRota.map(t => {
                      const selecionadas = colunasPorTabela[t.tabela_fonte] ?? [];
                      const todasSelecionadas =
                        t.colunas.length > 0 && selecionadas.length === t.colunas.length;
                      const algumaSelecionada = selecionadas.length > 0;
                      return (
                        <Box key={t.tabela_fonte} borderWidth="1px" borderRadius="md" p={2}>
                          <Flex align="center" justify="space-between" mb={1}>
                            <Text fontSize="sm" fontWeight="600">
                              {t.tabela_fonte}
                              <Text as="span" fontSize="xs" color="gray.400" ml={2}>
                                /external/v1/{t.slug}/
                              </Text>
                            </Text>
                            <Checkbox
                              size="sm"
                              isChecked={todasSelecionadas}
                              isIndeterminate={algumaSelecionada && !todasSelecionadas}
                              onChange={() =>
                                toggleTodasColunas(t.tabela_fonte, t.colunas, !todasSelecionadas)
                              }
                            >
                              <Text fontSize="xs" color="gray.500">
                                Selecionar tudo
                              </Text>
                            </Checkbox>
                          </Flex>
                          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={1}>
                            {t.colunas.map(col => (
                              <Checkbox
                                key={col}
                                size="sm"
                                isChecked={selecionadas.includes(col)}
                                onChange={() => toggleColuna(t.tabela_fonte, col)}
                              >
                                {col}
                              </Checkbox>
                            ))}
                          </SimpleGrid>
                        </Box>
                      );
                    })}
                  </Box>
                </FormControl>
              )}

              <FormControl>
                <FormLabel>3. Empresas (o token lê os dados de todas)</FormLabel>
                <EmpresaExternalIdSelect value={empresas} onChange={setEmpresas} />
              </FormControl>

              <FormControl>
                <FormLabel>4. Cliente (quem consome — para auditoria)</FormLabel>
                <ClienteUsuarioSelect value={cliente} onChange={setCliente} />
              </FormControl>

              <FormControl>
                <FormLabel>Nome do token</FormLabel>
                <Input
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                  placeholder="Ex: Integração Parceiro X"
                />
              </FormControl>
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          {criado ? (
            <Button onClick={onClose}>Concluir</Button>
          ) : (
            <>
              <Flex align="center" gap={1} mr="auto" color="yellow.700">
                <WarningCircle size={16} weight="fill" />
                <Text fontSize="xs">A key poderá ser revelada depois na lista de tokens.</Text>
              </Flex>
              <Button variant="outline" onClick={onClose} isDisabled={isPending}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} isLoading={isPending} isDisabled={!podeSalvar}>
                Criar token
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
